import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useRequireAdmin } from "@/hooks/useRequireAdmin";
import {
  getCompany,
  updateCompany,
  type CompanyDetails,
} from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Building2,
  Globe,
  Loader2,
  Save,
  CheckCircle2,
  Receipt,
} from "lucide-react";
import { SUPPORTED_LNGS } from "@/i18n/config";
import { normalizeWebsiteUrl, validateWebsite } from "@/lib/validation";
import {
  AddressAutocomplete,
  composeAddressDisplay,
} from "@/components/ui/address-autocomplete";
import type { StructuredAddress } from "@/components/ui/address-autocomplete";

interface FormState {
  name: string;
  website: string;
  // Firmenadresse (structured, stored in DB)
  company_street: string;
  company_postal_code: string;
  company_city: string;
  company_country: string;
  company_address_extra: string;
  // Display string for the typeahead input
  company_address_display: string;
  // Rechnungsadresse
  billing_same_as_company: boolean;
  billing_street: string;
  billing_postal_code: string;
  billing_city: string;
  billing_country: string;
  billing_address_extra: string;
  billing_contact_name: string;
  billing_email: string;
  vat_id: string;
  // Display string for billing typeahead
  billing_address_display: string;
}

const INITIAL_FORM: FormState = {
  name: "",
  website: "",
  company_street: "",
  company_postal_code: "",
  company_city: "",
  company_country: "DE",
  company_address_extra: "",
  company_address_display: "",
  billing_same_as_company: true,
  billing_street: "",
  billing_postal_code: "",
  billing_city: "",
  billing_country: "DE",
  billing_address_extra: "",
  billing_contact_name: "",
  billing_email: "",
  vat_id: "",
  billing_address_display: "",
};

function formFromCompany(c: CompanyDetails): FormState {
  const companyAddr: StructuredAddress = {
    street: c.company_street || "",
    postalCode: c.company_postal_code || "",
    city: c.company_city || "",
    country: c.company_country || "DE",
  };
  const billingAddr: StructuredAddress = {
    street: c.billing_street || "",
    postalCode: c.billing_postal_code || "",
    city: c.billing_city || "",
    country: c.billing_country || "DE",
  };
  return {
    name: c.name || "",
    website: c.website || "",
    company_street: companyAddr.street,
    company_postal_code: companyAddr.postalCode,
    company_city: companyAddr.city,
    company_country: companyAddr.country,
    company_address_extra: c.company_address_extra || "",
    company_address_display: composeAddressDisplay(companyAddr),
    billing_same_as_company: c.billing_same_as_company ?? true,
    billing_street: billingAddr.street,
    billing_postal_code: billingAddr.postalCode,
    billing_city: billingAddr.city,
    billing_country: billingAddr.country,
    billing_address_extra: c.billing_address_extra || "",
    billing_contact_name: c.billing_contact_name || "",
    billing_email: c.billing_email || "",
    vat_id: c.vat_id || "",
    billing_address_display: composeAddressDisplay(billingAddr),
  };
}

function isCompanyAddressComplete(form: FormState): boolean {
  return (
    form.company_street.trim() !== "" &&
    form.company_postal_code.trim() !== "" &&
    form.company_city.trim() !== "" &&
    form.company_country.trim() !== ""
  );
}

function isBillingAddressComplete(form: FormState): boolean {
  if (form.billing_same_as_company) return true;
  return (
    form.billing_street.trim() !== "" &&
    form.billing_postal_code.trim() !== "" &&
    form.billing_city.trim() !== "" &&
    form.billing_country.trim() !== ""
  );
}

export function isSaveDisabled(form: FormState): boolean {
  return (
    !form.name.trim() ||
    !isCompanyAddressComplete(form) ||
    !isBillingAddressComplete(form)
  );
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation("settings");
  const { isAdmin, isLoading: adminLoading } = useRequireAdmin();
  const { profile, refreshProfile } = useAuth();

  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!profile?.company_id || !isAdmin) return;

    async function load() {
      try {
        const c = await getCompany(profile!.company_id!);
        setCompany(c);
        setForm(formFromCompany(c));
      } catch (err) {
        setError(err instanceof Error ? err.message : t("errors.loadFailed"));
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [profile?.company_id, isAdmin, t]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!profile?.company_id) return;
    setError(null);
    setSaved(false);
    setSaving(true);

    try {
      const payload: Parameters<typeof updateCompany>[1] = {
        name: form.name,
        website: normalizeWebsiteUrl(form.website) || null,
        company_street: form.company_street || null,
        company_postal_code: form.company_postal_code || null,
        company_city: form.company_city || null,
        company_country: form.company_country || null,
        company_address_extra: form.company_address_extra || null,
        billing_same_as_company: form.billing_same_as_company,
      };

      if (!form.billing_same_as_company) {
        payload.billing_street = form.billing_street || null;
        payload.billing_postal_code = form.billing_postal_code || null;
        payload.billing_city = form.billing_city || null;
        payload.billing_country = form.billing_country || null;
        payload.billing_address_extra = form.billing_address_extra || null;
        payload.billing_contact_name = form.billing_contact_name || null;
        payload.billing_email = form.billing_email || null;
        payload.vat_id = form.vat_id || null;
      }

      const updated = await updateCompany(profile.company_id, payload);
      setCompany(updated);
      setSaved(true);
      await refreshProfile();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  if (adminLoading || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ── Card 1: Firmendaten ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-primary" />
              {t("sections.companyData.title")}
            </CardTitle>
            <CardDescription>
              {t("sections.companyData.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {saved && (
              <Alert className="border-green-200 bg-green-50 text-green-800">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{t("success.saved")}</AlertDescription>
              </Alert>
            )}

            {/* Firmenname */}
            <div className="space-y-2">
              <Label htmlFor="name">{t("form.companyName")}</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                required
              />
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website">{t("form.website.label")}</Label>
              <Input
                id="website"
                type="text"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
                placeholder={t("form.website.placeholder")}
              />
              {form.website && validateWebsite(form.website) && (
                <p className="text-xs text-destructive">
                  {validateWebsite(form.website)}
                </p>
              )}
              {company?.domain && (
                <p className="text-xs text-muted-foreground">
                  Domain: {company.domain}
                </p>
              )}
            </div>

            {/* ── Firmenadresse ── */}
            <div className="space-y-3 pt-2">
              <p className="text-sm font-medium">
                {t("form.companyAddress.label")}
              </p>

              {company?.needs_address_review && (
                <Alert>
                  <AlertDescription className="space-y-1">
                    <span>{t("form.companyAddress.reviewWarning")}</span>
                    {company.address_legacy && (
                      <span className="block text-xs text-muted-foreground">
                        {t("form.companyAddress.legacyAddress", {
                          address: company.address_legacy,
                        })}
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Single address typeahead */}
              <div className="space-y-2">
                <Label htmlFor="company_address">
                  {t("form.address.label")}
                </Label>
                <AddressAutocomplete
                  id="company_address"
                  value={form.company_address_display}
                  onChange={(val) => setField("company_address_display", val)}
                  onStructuredChange={(addr: StructuredAddress) => {
                    const display = composeAddressDisplay(addr);
                    setForm((prev) => ({
                      ...prev,
                      company_street: addr.street,
                      company_postal_code: addr.postalCode,
                      company_city: addr.city,
                      company_country: addr.country || prev.company_country,
                      // Only update display when we have real data (suggestion selected).
                      // When clearing (manual edit), onChange already handles the display.
                      ...(display ? { company_address_display: display } : {}),
                    }));
                  }}
                  structuredValue={{
                    street: form.company_street,
                    postalCode: form.company_postal_code,
                    city: form.company_city,
                    country: form.company_country,
                  }}
                  placeholder={t("form.address.placeholder")}
                  required
                />
              </div>

              {/* Adresszusatz */}
              <div className="space-y-2">
                <Label htmlFor="company_address_extra">
                  {t("form.addressExtra.label")}
                </Label>
                <Input
                  id="company_address_extra"
                  value={form.company_address_extra}
                  onChange={(e) =>
                    setField("company_address_extra", e.target.value)
                  }
                  placeholder={t("form.addressExtra.placeholder")}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Card 2: Rechnungsadresse ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-4 w-4 text-primary" />
              {t("sections.billingAddress.title")}
            </CardTitle>
            <CardDescription>
              {t("sections.billingAddress.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Toggle: same as company */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="billing_same_as_company"
                checked={form.billing_same_as_company}
                onChange={(e) =>
                  setField("billing_same_as_company", e.target.checked)
                }
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <Label
                htmlFor="billing_same_as_company"
                className="cursor-pointer font-normal"
              >
                {t("form.billingAddress.sameAsCompany")}
              </Label>
            </div>

            {form.billing_same_as_company ? (
              <p className="text-sm text-muted-foreground">
                {t("form.billingAddress.info")}
              </p>
            ) : (
              <div className="space-y-4">
                {/* Rechnungsempfänger */}
                <div className="space-y-2">
                  <Label htmlFor="billing_contact_name">
                    {t("form.billingContact.label")}
                  </Label>
                  <Input
                    id="billing_contact_name"
                    value={form.billing_contact_name}
                    onChange={(e) =>
                      setField("billing_contact_name", e.target.value)
                    }
                    placeholder={t("form.billingContact.placeholder")}
                  />
                </div>

                {/* Rechnungs-E-Mail */}
                <div className="space-y-2">
                  <Label htmlFor="billing_email">
                    {t("form.billingEmail.label")}
                  </Label>
                  <Input
                    id="billing_email"
                    type="email"
                    value={form.billing_email}
                    onChange={(e) => setField("billing_email", e.target.value)}
                    placeholder={t("form.billingEmail.placeholder")}
                  />
                </div>

                {/* Single billing address typeahead */}
                <div className="space-y-2">
                  <Label htmlFor="billing_address">
                    {t("form.billingAddress.label")}
                  </Label>
                  <AddressAutocomplete
                    id="billing_address"
                    value={form.billing_address_display}
                    onChange={(val) => setField("billing_address_display", val)}
                    onStructuredChange={(addr: StructuredAddress) => {
                      const display = composeAddressDisplay(addr);
                      setForm((prev) => ({
                        ...prev,
                        billing_street: addr.street,
                        billing_postal_code: addr.postalCode,
                        billing_city: addr.city,
                        billing_country: addr.country || prev.billing_country,
                        ...(display
                          ? { billing_address_display: display }
                          : {}),
                      }));
                    }}
                    structuredValue={{
                      street: form.billing_street,
                      postalCode: form.billing_postal_code,
                      city: form.billing_city,
                      country: form.billing_country,
                    }}
                    placeholder={t("form.billingAddress.placeholder")}
                    required
                  />
                </div>

                {/* Adresszusatz */}
                <div className="space-y-2">
                  <Label htmlFor="billing_address_extra">
                    {t("form.billingAddressExtra.label")}
                  </Label>
                  <Input
                    id="billing_address_extra"
                    value={form.billing_address_extra}
                    onChange={(e) =>
                      setField("billing_address_extra", e.target.value)
                    }
                    placeholder={t("form.billingAddressExtra.placeholder")}
                  />
                </div>

                {/* USt-IdNr. */}
                <div className="space-y-2">
                  <Label htmlFor="vat_id">{t("form.vatId.label")}</Label>
                  <Input
                    id="vat_id"
                    value={form.vat_id}
                    onChange={(e) => setField("vat_id", e.target.value)}
                    placeholder={t("form.vatId.placeholder")}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        {/* ── Save button (applies to both cards) ── */}
        <div className="flex justify-end">
          <Button type="submit" disabled={saving || isSaveDisabled(form)}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {t("form.submitButton")}
          </Button>
        </div>
      </form>

      {/* ── Card 3: Language (outside form — instant switch via i18n) ── */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4 text-primary" />
            {t("sections.language.title")}
          </CardTitle>
          <CardDescription>
            {t("sections.language.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {SUPPORTED_LNGS.map((lng) => (
              <Button
                key={lng}
                type="button"
                variant={i18n.language === lng ? "default" : "outline"}
                size="sm"
                onClick={() => i18n.changeLanguage(lng)}
              >
                {lng === "de" ? "Deutsch" : "English"}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
